import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RATING_KIND_VERSION, pickRating } from "../../utils/ratingDisplay";
import PlayerImage from "../../components/PlayerImage";
import { FantasyPlayer } from "../../models/FantasyPlayer";
import { FantasyRarity } from "../../models/FantasyRarity";
import { divisionMappings } from "../../Constants";
import styles from './PlayerSlotV2.module.css';

interface PlayerSlotV2Props {
    player: FantasyPlayer;
    isSelected?: boolean;
    onPlayerAddClick?: (player: FantasyPlayer) => void;
    onPlayerRemoveClick?: (player: FantasyPlayer) => void;
    locked?: boolean;
    compact?: boolean;
}

const RARITY_LABELS: Record<number, string> = {
    [FantasyRarity.Bronze]: 'Bronze',
    [FantasyRarity.Silver]: 'Silver',
    [FantasyRarity.ShinySilver]: 'Shiny Silver',
    [FantasyRarity.Gold]: 'Gold',
    [FantasyRarity.ShinyGold]: 'Shiny Gold',
};

const RARITY_CLASSES: Record<number, string> = {
    [FantasyRarity.Bronze]: styles.Bronze,
    [FantasyRarity.Silver]: styles.Silver,
    [FantasyRarity.ShinySilver]: styles.ShinySilver,
    [FantasyRarity.Gold]: styles.Gold,
    [FantasyRarity.ShinyGold]: styles.ShinyGold,
};

const isShiny = (rarity: FantasyRarity) =>
    rarity === FantasyRarity.ShinyGold || rarity === FantasyRarity.ShinySilver;

const getStatPercent = (key: string, value: number | undefined | null): number => {
    if (value == null || isNaN(value)) return 0;
    const maxes: Record<string, number> = {
        rating: 1.8, kd: 1.8, fk: 80, kast: 100, adr: 120, hs: 100, faceit: 3700,
    };
    return Math.min(100, Math.max(0, (value / (maxes[key] || 100)) * 100));
};

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', minimumFractionDigits: 0 }).format(amount);

const formatDiv = (div: string | null) => div ? (divisionMappings.get(div) || div) : '';

const PlayerSlotV2: React.FC<PlayerSlotV2Props> = ({ player, isSelected, onPlayerAddClick, onPlayerRemoveClick, locked, compact }) => {
    const [flipped, setFlipped] = useState(false);
    const [showDivTooltip, setShowDivTooltip] = useState(false);
    const { t } = useTranslation(['pages', 'stats']);

    const handleFlip = () => setFlipped(prev => !prev);

    const rarityClass = RARITY_CLASSES[player.rarity] ?? '';
    const shinyClass = isShiny(player.rarity) ? styles.shiny : '';
    const rarityLabel = RARITY_LABELS[player.rarity] ?? '';

    // Rating 3.0 where the season has it, else 2.1, else 2.0.
    const pickedRating = pickRating(player.stats);

    const stats = useMemo(() => [
        {
            label: 'Rating', key: 'rating',
            value: pickedRating?.value,
            display: pickedRating ? pickedRating.value.toFixed(2) + (pickedRating.kind === 'r3' ? '' : '*') : '—',
            // .statLabel is a fixed 48px at 10px, so "Rating 3.0" can't go in the label without
            // shifting every row. Asterisk + tooltip instead, same as the LFT card.
            title: pickedRating && pickedRating.kind !== 'r3'
                ? t('stats:ratingFallbackNote', { version: RATING_KIND_VERSION[pickedRating.kind] })
                : undefined,
        },
        { label: 'K/D', key: 'kd', value: player.stats?.kd, display: player.stats?.kd?.toFixed(2) ?? '—' },
        { label: 'FK', key: 'fk', value: player.stats?.fk, display: player.stats?.fk?.toString() ?? '—' },
        { label: 'KAST', key: 'kast', value: player.stats?.kast, display: player.stats?.kast ? `${player.stats.kast.toFixed(1)}%` : '—' },
        { label: 'ADR', key: 'adr', value: player.stats?.adr, display: player.stats?.adr?.toFixed(1) ?? '—' },
        { label: 'HS%', key: 'hs', value: player.stats?.hs, display: player.stats?.hs ? `${player.stats.hs.toFixed(1)}%` : '—' },
        { label: 'Faceit', key: 'faceit', value: player.account.faceitElo, display: player.account.faceitElo?.toString() ?? '—' },
    ], [player.stats, player.account.faceitElo, pickedRating, t]);

    const divAdj = player.divisionAdjustmentPercent;

    return (
        <div
            className={`${styles.cardV2} ${rarityClass} ${shinyClass} ${flipped ? styles.flipped : ''} ${compact ? styles.compact : ''}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleFlip(); } }}
        >
            <div className={styles.cardInner}>
                {/* ===== FRONT ===== */}
                <div className={`${styles.cardFace} ${styles.cardFront}`}>
                    <span className={styles.rarityLabel}>{rarityLabel}</span>

                    <div className={styles.playerImageFrame} onClick={handleFlip}>
                        <PlayerImage id={player.account.steamID} width={200} />
                    </div>

                    <div className={styles.bottomOverlay}>
                        <div className={styles.playerName}>{player.account.playerName}</div>

                        <div className={styles.priceRow}>
                            <span className={styles.price}>{formatCurrency(player.price)}</span>
                            {divAdj !== 0 && (
                                <span
                                    className={`${styles.divisionBadge} ${divAdj < 0 ? styles.divisionBadgeUp : styles.divisionBadgeDown}`}
                                    onMouseEnter={() => setShowDivTooltip(true)}
                                    onMouseLeave={() => setShowDivTooltip(false)}
                                >
                                    {divAdj < 0 ? '↑' : '↓'} {divAdj > 0 ? '+' : ''}{divAdj}%
                                </span>
                            )}
                        </div>

                        {(onPlayerAddClick || onPlayerRemoveClick || isSelected) && (
                            <div className={styles.actionArea}>
                                {isSelected ? (
                                    locked ? (
                                        <span className={styles.lockedBadge}>{player.role}</span>
                                    ) : onPlayerRemoveClick ? (
                                        <button
                                            className={styles.removeBtn}
                                            onClick={(e) => { e.stopPropagation(); onPlayerRemoveClick(player); }}
                                        >
                                            Remove Player
                                        </button>
                                    ) : (
                                        <span className={styles.lockedBadge}>Valgt</span>
                                    )
                                ) : onPlayerAddClick ? (
                                    <button
                                        className={styles.addBtn}
                                        onClick={(e) => { e.stopPropagation(); onPlayerAddClick(player); }}
                                    >
                                        Vælg spiller
                                    </button>
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>

                {/* ===== BACK ===== */}
                <div className={`${styles.cardFace} ${styles.cardBack}`} onClick={handleFlip}>
                    <div className={styles.rarityBar}>STATS</div>
                    <div className={styles.backName}>{player.account.playerName}</div>
                    <div className={styles.statsContainer}>
                        {stats.map(s => (
                            <div className={styles.statRow} key={s.key}>
                                <span className={styles.statLabel}>{s.label}</span>
                                <div className={styles.statBar}>
                                    <div
                                        className={styles.statBarFill}
                                        style={{ width: `${getStatPercent(s.key, s.value)}%` }}
                                    />
                                </div>
                                <span className={styles.statValue} title={s.title}>{s.display}</span>
                            </div>
                        ))}
                    </div>
                    <a
                        href={`/player/${player.account.steamID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.profileLink}
                        onClick={(e) => e.stopPropagation()}
                    >
                        View Profile
                    </a>
                </div>
            </div>

            {/* Division adjustment tooltip — rendered outside cardInner to escape overflow:hidden */}
            {showDivTooltip && divAdj !== 0 && (
                <div className={styles.divisionTooltip}>
                    <span className={styles.tooltipTitle}>
                        {t('pages:fantasy.priceAdjusted')}: {divAdj > 0 ? '+' : ''}{divAdj}%
                    </span>
                    <span className={styles.tooltipDivisions}>
                        <span>{t('pages:fantasy.previouslyIn')}: <strong>{formatDiv(player.previousDivision)}</strong></span>
                        <span>{t('pages:fantasy.nowIn')}: <strong>{formatDiv(player.currentDivision)}</strong></span>
                    </span>
                    <span className={styles.tooltipExplain}>
                        {divAdj < 0 ? t('pages:fantasy.movedUp') : t('pages:fantasy.movedDown')}
                        {' '}(±5% {t('pages:fantasy.perDivision')})
                    </span>
                </div>
            )}
        </div>
    );
};

export default PlayerSlotV2;
